/* eslint-disable no-unused-vars */
import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  IconButton,
  Button,
  useColorModeValue,
  Switch,
  Tooltip,
} from '@chakra-ui/react';

import {
  EditIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
} from '@chakra-ui/icons';
import { FiGrid, FiList } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

// 🎬 Motion Components
const MotionTr = motion(Tr);
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const DevelopmentTable = ({
  tableData,
  handlePageChange,
  page,
  totalPages,
  loading,
  topActionButton,
  onEdit,
}) => {
  const navigate = useNavigate();
  // 🌈 UI Vars
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const cardBg = useColorModeValue('white', 'gray.900');
  const borderCol = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('blue.50', 'gray.800');
  const headerText = useColorModeValue('blue.700', 'blue.200');
  const rowHover = useColorModeValue('blue.50', 'gray.700');
  const inactiveBg = useColorModeValue('red.50', 'red.900');
  const zebraBg = useColorModeValue('gray.50', 'gray.800');

  <style>
    {`
    .shimmer {
      background: linear-gradient(
        90deg,
        #e0e0e0 0%,
        #f5f5f5 50%,
        #e0e0e0 100%
      );
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `}
  </style>;

  // 🌟 Table Rows Rendering
  const renderTableRows = () => {
    if (loading) {
      return Array.from({ length: 6 })?.map((_, index) => (
        <Tr key={index}>
          {Array.from({ length: 8 })?.map((_, colIndex) => (
            <Td key={colIndex}>
              <Box height="20px" className="shimmer" borderRadius="6px" />
            </Td>
          ))}
        </Tr>
      ));
    }

    return tableData?.map((doc, index) => (
      <MotionTr
        key={doc._id}
        bg={
          doc.active === false
            ? inactiveBg
            : index % 2 === 0
              ? zebraBg
              : 'transparent'
        }
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.05 }}
        whileHover={{
          scale: 1.0006,
          backgroundColor: rowHover,
          transition: { duration: 0.2 },
        }}
      >
        <Td>{index + 1}</Td>

        <Td>
          <Text fontWeight="700">{doc.remedyCode}</Text>
        </Td>

        <Td>
          <Text fontWeight="600" color="blue.600">
            {doc.remedyName || 'General'}
          </Text>
        </Td>

        <Td>{doc.polarity || 'N/A'}</Td>
        <Td>{doc.primaryAxis || 'N/A'}</Td>
        <Td>{doc.secondaryAxis || 'N/A'}</Td>
        <Td>{doc.defaultElectricity || 'N/A'}</Td>
        <Td maxW="280px">
          <Tooltip label={doc.coreAction}>
            <Text noOfLines={2} cursor="pointer">
              {doc.coreAction}
            </Text>
          </Tooltip>
        </Td>
        <Td maxW="280px">
          <Tooltip label={doc.coreAction}>
            <Text noOfLines={2} cursor="pointer">
              {doc.clinicalTags}
            </Text>
          </Tooltip>
        </Td>

        <Td>{doc.polarityLevel || 'N/A'}</Td>

        <Td>
          <Flex justify="center" alignItems="center" gap={4}>
            <IconButton
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(doc);
              }}
            />
          </Flex>
        </Td>
      </MotionTr>
    ));
  };

  // 🌟 Pagination
  const renderPagination = () => (
    <MotionFlex
      justify="center"
      align="center"
      py={0.1}
      gap={3}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <IconButton
        icon={<ChevronLeftIcon />}
        size="sm"
        onClick={() => handlePageChange(page - 1)}
        isDisabled={page <= 1}
      />

      <Text fontWeight="600">
        Page {page} of {totalPages}
      </Text>

      <IconButton
        icon={<ChevronRightIcon />}
        size="sm"
        onClick={() => handlePageChange(page + 1)}
        isDisabled={page >= totalPages}
      />
    </MotionFlex>
  );

  return (
    <>
      {/* HEADER + RIGHT BUTTON */}
      <Flex align="center" mb={4}>
        <Text fontSize="22px" fontWeight="bold" color={textColor}>
          {topActionButton?.title || 'Users'}
        </Text>

        {topActionButton && (
          <Button
            leftIcon={topActionButton.icon || <AddIcon />}
            colorScheme="blue"
            onClick={topActionButton.onClick}
            ml="auto" // 👈 push extreme right
          >
            {topActionButton.label}
          </Button>
        )}
      </Flex>
      <Card
        w="100%"
        bg={cardBg}
        border="1px solid"
        borderColor={borderCol}
        shadow="lg"
        borderRadius="lg"
        p={2}
      >
        <Box overflowX="auto" maxHeight="calc(100vh - 280px)">
          <Table variant="simple" size="md" sx={{ tableLayout: 'fixed' }}>
            <Thead bg={headerBg} position="sticky" top={0} zIndex={10}>
              <Tr>
                <Th w="100px">Sr. No.</Th>
                <Th w="120px">Remedy Code</Th>
                <Th w="180px">Remedy Name</Th>
                <Th w="140px">Polarity</Th>
                <Th w="160px">Primary Axis</Th>
                <Th w="160px">Secondary Axis</Th>
                <Th w="160px">Default Electricity</Th>
                <Th w="300px">Core Action</Th>
                <Th w="300px">Clinical Tags</Th>
                <Th w="120px">Priority Level</Th>
                <Th w="110px" textAlign="center">
                  Actions
                </Th>
              </Tr>
            </Thead>

            <Tbody>{renderTableRows()}</Tbody>
          </Table>
        </Box>

        {renderPagination()}
      </Card>
    </>
  );
};

DevelopmentTable.propTypes = {
  tableData: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  handleToggleActive: PropTypes.func.isRequired,
  topActionButton: PropTypes.shape({
    title: PropTypes.string,
    label: PropTypes.string.isRequired,
    icon: PropTypes.node,
    onClick: PropTypes.func.isRequired,
  }),
};

export default React.memo(DevelopmentTable);
